import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/outline";
import { CheckCircleIcon, XCircleIcon, XIcon } from "@heroicons/react/solid";
import React, { useMemo, useState } from "react";
import classNames from "../utils/classNames";
import ExclamationIcon from "./icons/ExclamationIcon";
import InfoIcon from "./icons/InfoIcon";

export type AlertType = "info" | "warning" | "success" | "error";

interface Props {
  className?: string;
  type: AlertType;
  title?: any;
  message?: any;
  onClose?: () => void;
  collapsible?: boolean;
}

const AlertMessage: React.FC<Props> = ({
  className = "",
  type,
  title,
  message,
  onClose,
  collapsible
}) => {
  const [isCollapsed, setIsCollapsed] = useState(collapsible);

  const [classNamesMap, Icon] = useMemo(() => {
    switch (type) {
      case "error":
        return [
          {
            background: "bg-red-50",
            icon: "text-red-400",
            title: "text-red-800",
            message: "text-red-700",
            actionButton: "text-red-500 hover:bg-red-100"
          },
          XCircleIcon
        ];
      case "warning":
        return [
          {
            background: "bg-yellow-50",
            icon: "text-yellow-400",
            title: "text-yellow-800",
            message: "text-yellow-700",
            actionButton: "text-yellow-500 hover:bg-yellow-100"
          },
          ExclamationIcon
        ];
      case "success":
        return [
          {
            background: "bg-green-50",
            icon: "text-green-400",
            title: "text-green-800",
            message: "text-green-700",
            actionButton: "text-green-500 hover:bg-green-100"
          },
          CheckCircleIcon
        ];
      case "info":
      default:
        return [
          {
            background: "bg-hotspotty-50",
            icon: "text-hotspotty-400",
            title: "text-hotspotty-800",
            message: "text-hotspotty-700",
            actionButton: "text-hotspotty-400 hover:bg-hotspotty-100"
          },
          InfoIcon
        ];
    }
  }, [type]);

  const renderOnClose = useMemo(
    () => (
      <div className="ml-auto pl-3">
        <div className="-mx-1.5 -my-1.5">
          <button
            type="button"
            onClick={onClose}
            className={classNames(
              "inline-flex rounded-md p-1.5 focus:outline-none dark:text-white dark:hover:text-gray-800",
              classNamesMap.actionButton
            )}
          >
            <XIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>
    ),
    [classNamesMap.actionButton, onClose]
  );

  const renderCollapseButton = useMemo(
    () => (
      <div className="ml-auto pl-3">
        <div className="-mx-1.5 -my-1.5">
          <button
            type="button"
            onClick={() => setIsCollapsed((prevValue) => !prevValue)}
            className={classNames(
              "inline-flex rounded-md p-1.5 focus:outline-none dark:text-white dark:hover:text-gray-800",
              classNamesMap.actionButton
            )}
          >
            {isCollapsed ? (
              <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
            ) : (
              <ChevronUpIcon className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>
    ),
    [classNamesMap.actionButton, isCollapsed]
  );

  return (
    <div
      className={classNames(
        "mb-6 rounded-md dark:bg-gray-800 p-4",
        classNamesMap.background,
        className
      )}
    >
      <div className="flex">
        <div className="flex-shrink-0">
          <Icon className={classNames("h-5 w-5", classNamesMap.icon)} />
        </div>

        {title && (
          <h3
            className={classNames(
              "ml-2 text-sm font-medium dark:text-white",
              classNamesMap.title
            )}
          >
            {title}
          </h3>
        )}

        {!!onClose && renderOnClose}

        {!!collapsible && renderCollapseButton}
      </div>

      {!isCollapsed && message && (
        <div
          className={classNames(
            "mt-2 text-sm dark:text-gray-50",
            classNamesMap.message
          )}
        >
          {message}
        </div>
      )}
    </div>
  );
};

export default AlertMessage;
